
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { IProvider } from "@/models/providers";
import {
  IObjectNotificationProvider,
  IObjectNotificationProviderMethods
} from "@/models/providers/objectNotifications";
import { CreateElement } from "vue";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import {
  INotificationCategory,
  INotificationTemplate
} from "@/models/notifications";
import { ObjectNotificationTypes } from "@/data/enums/objectNotifications";

@Component
export default class ObjectNotificationProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ default: null })
  public mappingId!: string;

  @Prop({ required: true })
  public objectType!: ObjectNotificationTypes;

  @Prop({ required: true })
  public objectId!: string;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public isLoading = true;
  public hasRequestError = false;
  public isProcessing = false;
  public isReloading = false;
  public templateCategories: INotificationCategory[] = [];
  public templates: INotificationTemplate[] = [];

  @Provide("objectNotificationMethods")
  public get providerMethods(): IObjectNotificationProviderMethods {
    return {
      reloadData: this.reloadData,
      create: this.create,
      update: this.update,
      setProcessing: this.setProcessing,
      setTemplates: this.setTemplates
    };
  }

  @Provide("objectNotificationProvider")
  public providerData(): IObjectNotificationProvider {
    return {
      objectType: this.objectType,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      isProcessing: this.isProcessing,
      templateCategories: this.templateCategories,
      templates: this.templates
    };
  }

  // Life-cycle methods
  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    // Empty
  }

  // Methods

  public setProcessing(value) {
    this.isProcessing = value;
  }

  public reloadData() {
    try {
      this.isReloading = true;
      return this.loadData();
    } finally {
      this.isReloading = false;
    }
  }

  public async loadData() {
    try {
      this.templateCategories = await this.getTemplateCategories();
      await this.setTemplates();
    } finally {
      this.isLoading = false;
    }
  }

  public async setTemplates() {
    this.templates = await this.getTemplates();
  }

  public create(form: any) {
    return this.$store.dispatch(
      `data/${DataModules.OBJECT_NOTIFICATIONS}/create`,
      {
        objectType: this.objectType,
        objectId: this.objectId,
        data: form
      }
    );
  }

  public update(form: any) {
    return this.$store.dispatch(
      `data/${DataModules.OBJECT_NOTIFICATIONS}/update`,
      {
        id: this.mappingId,
        objectType: this.objectType,
        objectId: this.objectId,
        data: form
      }
    );
  }

  public getTemplateCategories() {
    return this.$store.dispatch(
      `data/${DataModules.OBJECT_NOTIFICATIONS}/templateCategories`,
      {
        vm: this,
        storeData: false,
        objectType: this.objectType,
        params: {
          limit: 0,
          order: "name",
          with: "hooks"
        }
      }
    );
  }

  public getTemplates() {
    return this.$store.dispatch(
      `data/${DataModules.OBJECT_NOTIFICATIONS}/templates`,
      {
        vm: this,
        storeData: false,
        objectType: this.objectType,
        params: {
          limit: 0,
          order: "name",
          with: "category"
        }
      }
    );
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $objNotificationData: this.providerData(),
        $objNotificationMethods: this.providerMethods
      })
    );
  }
}
